import { useEffect, useState } from "react";
import { Calculator, Download, Minus, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export default function PendapatanKotor() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [monthsN, setMonthsN] = useState("1");
  const [data, setData] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = (m, n) => {
    api.get(`/reports/pendapatan-kotor?month=${m}&months=${n}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(month, monthsN); }, [month, monthsN]);

  const BULAN_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
  const fmtMo = (ym) => { const [y, m] = (ym || "").split("-"); return `${BULAN_ID[Number(m) - 1] || ""} ${y || ""}`; };
  const periodLabel = data ? (data.months > 1 ? `${fmtMo(data.start)} – ${fmtMo(data.month)}` : fmtMo(data.month)) : fmtMo(month);

  const downloadPdf = async () => {
    if (!data) return;
    setPdfBusy(true);
    try {
      const { jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");
      const doc = new jsPDF();
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("FASNET FIBER — Laporan Pendapatan Kotor", 14, 16);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Periode: ${periodLabel}${data.months > 1 ? ` (${data.months} bulan)` : ""}`, 14, 23);
      doc.text(`Digenerate: ${new Date().toLocaleString("id-ID")}`, 14, 29);
      const t = data.totals;
      autoTable(doc, {
        startY: 35,
        head: [["Rincian Fasnet Fiber", "Nominal"]],
        body: [
          ["Omset Bulanan Seluruh Cabang", rupiah(t.omset)],
          ["Biaya Bandwidth Seluruh Cabang (Starlink + Indibiz)", `- ${rupiah(t.bandwidth)}`],
          ["Seluruh Komisi Agen", `- ${rupiah(t.komisi)}`],
          ["Belanja Stok", `- ${rupiah(t.belanja_stok)}`],
          ["PENDAPATAN KOTOR", rupiah(t.kotor)],
        ],
        styles: { fontSize: 9 },
        headStyles: { fillColor: [240, 78, 35] },
        columnStyles: { 1: { halign: "right" } },
      });
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [["Cabang", "Omset", "Biaya Bandwidth", "Komisi", "Pendapatan Kotor"]],
        body: [
          ...data.rows.map((r) => [r.name, rupiah(r.omset), r.bandwidth ? `- ${rupiah(r.bandwidth)}` : "-", r.komisi ? `- ${rupiah(r.komisi)}` : "-", rupiah(r.kotor)]),
          [`TOTAL + Belanja Stok (${rupiah(t.belanja_stok)})`, rupiah(t.omset), `- ${rupiah(t.bandwidth)}`, `- ${rupiah(t.komisi)}`, rupiah(t.kotor)],
        ],
        styles: { fontSize: 8 },
        headStyles: { fillColor: [30, 30, 30] },
        columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } },
      });
      doc.save(`pendapatan-kotor-${data.start}_sd_${data.month}.pdf`);
      toast.success("PDF laporan diunduh");
    } catch { toast.error("Gagal membuat PDF"); } finally { setPdfBusy(false); }
  };

  const t = data?.totals;

  const Step = ({ label, value, minus, bold, accent, testid }) => (
    <div className="flex items-center justify-between gap-4 py-1.5" data-testid={testid}>
      <span className={`text-xs ${bold ? "font-bold text-foreground" : "text-muted-foreground"} flex items-center gap-1.5`}>
        {minus && <Minus className="w-3 h-3 text-rose-400" />}{label}
      </span>
      <span className={`font-mono text-sm whitespace-nowrap ${accent ? "text-emerald-400 font-bold text-base" : bold ? "font-bold" : ""} ${minus ? "text-rose-400" : ""}`}>
        {minus && value > 0 ? "-" : ""}{rupiah(value)}
      </span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="pendapatan-kotor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Calculator className="w-7 h-7 text-[#F04E23]" /> Pendapatan Kotor</h1>
          <p className="text-sm text-muted-foreground mt-1">Per cabang: Omset − Biaya Bandwidth − Komisi. Keseluruhan (Fasnet Fiber): juga dikurangi Belanja Stok.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-[11px]">Periode</Label>
            <Select value={monthsN} onValueChange={setMonthsN}>
              <SelectTrigger className="w-28 h-9" data-testid="select-pk-months"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["1", "2", "3", "6"].map((n) => <SelectItem key={n} value={n} data-testid={`pk-months-opt-${n}`}>{n} Bulan</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Bulan Akhir</Label>
            <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-40 h-9" data-testid="input-pk-month" />
          </div>
          <Button variant="outline" className="h-9" onClick={downloadPdf} disabled={pdfBusy || !data} data-testid="btn-pk-pdf"><Download className="w-4 h-4 mr-1.5" /> {pdfBusy ? "Membuat..." : "Unduh PDF"}</Button>
        </div>
      </div>

      <Card className="bg-card border-border shadow-sm border-l-4 border-l-emerald-500" data-testid="pk-total-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> Omset Fasnet Fiber — {periodLabel}</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border/60">
          <Step label="Omset Bulanan Seluruh Cabang" value={t?.omset || 0} testid="pk-total-omset" />
          <Step label="Biaya Bandwidth Seluruh Cabang (Starlink + Indibiz)" value={t?.bandwidth || 0} minus testid="pk-total-bandwidth" />
          <Step label="Seluruh Komisi Agen" value={t?.komisi || 0} minus testid="pk-total-komisi" />
          <Step label="Belanja Stok" value={t?.belanja_stok || 0} minus testid="pk-total-belanja" />
          <Step label="Pendapatan Kotor" value={t?.kotor || 0} bold accent testid="pk-total-kotor" />
        </CardContent>
      </Card>

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="pk-table-card">
        <CardHeader><CardTitle className="text-base font-heading">Rincian per Cabang — {periodLabel}</CardTitle></CardHeader>
        <Table className="min-w-[760px]" data-testid="pk-table">
          <TableHeader>
            <TableRow>
              <TableHead>Cabang</TableHead>
              <TableHead className="text-right">Omset Bulanan</TableHead>
              <TableHead className="text-right">Biaya Bandwidth</TableHead>
              <TableHead className="text-right">Komisi</TableHead>
              <TableHead className="text-right">Pendapatan Kotor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!(data?.rows || []).length && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground" data-testid="pk-empty">Belum ada data untuk bulan ini</TableCell></TableRow>}
            {(data?.rows || []).map((r) => (
              <TableRow key={r.router_id || r.name} data-testid={`pk-row-${r.router_id || r.name}`}>
                <TableCell className="font-semibold text-xs whitespace-nowrap">{r.name}</TableCell>
                <TableCell className="text-right font-mono text-xs" data-testid={`pk-omset-${r.router_id || r.name}`}>{rupiah(r.omset)}</TableCell>
                <TableCell className="text-right font-mono text-xs text-rose-400" data-testid={`pk-bandwidth-${r.router_id || r.name}`}>{r.bandwidth ? `- ${rupiah(r.bandwidth)}` : "-"}</TableCell>
                <TableCell className="text-right font-mono text-xs text-rose-400" data-testid={`pk-komisi-${r.router_id || r.name}`}>{r.komisi ? `- ${rupiah(r.komisi)}` : "-"}</TableCell>
                <TableCell className={`text-right font-mono text-xs font-bold ${r.kotor >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid={`pk-kotor-${r.router_id || r.name}`}>{rupiah(r.kotor)}</TableCell>
              </TableRow>
            ))}
            {(data?.rows || []).length > 0 && t && (
              <TableRow className="bg-accent/40 font-bold" data-testid="pk-row-total">
                <TableCell className="text-xs font-bold">TOTAL + Belanja Stok ({rupiah(t.belanja_stok)})</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold">{rupiah(t.omset)}</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold text-rose-400">- {rupiah(t.bandwidth)}</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold text-rose-400">- {rupiah(t.komisi)}</TableCell>
                <TableCell className={`text-right font-mono text-sm font-bold ${t.kotor >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{rupiah(t.kotor)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <p className="text-[11px] text-muted-foreground" data-testid="pk-note">Biaya bandwidth diambil otomatis dari "Biaya Langganan/Bulanan" yang diisi admin di Data Starlink & Data Indibiz (nama cabang dicocokkan dengan nama router). Komisi diambil dari komisi tercatat pada penjualan bulan ini. Belanja stok dari menu Order Alat.</p>
    </div>
  );
}
